import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { ArrowLeft, Sparkles, History, Calendar, TrendingUp, Loader2, FileText, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../contexts/AuthContext";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HistoryPage() {
  const { t, i18n } = useTranslation();
  const { user, token } = useAuth();
  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(true);

  const isFrench = i18n.language === 'fr';

  useEffect(() => {
    if (token) {
      fetchDiagnostics();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchDiagnostics = async () => {
    try {
      const response = await axios.get(`${API}/user/diagnostics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiagnostics(response.data);
    } catch (error) {
      console.error("Error fetching diagnostics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(isFrench ? 'fr-FR' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-[#A8C3A0]";
    if (score >= 60) return "text-[#2F4F4F]";
    if (score >= 40) return "text-[#E2725B]";
    return "text-red-500";
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-6">
        <div className="text-center">
          <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-semibold text-[#2D2D2D] mb-4">
            {isFrench ? "Connectez-vous pour voir votre historique" : "Log in to see your history"}
          </h1>
          <Link to="/">
            <Button className="rounded-full bg-[#2F4F4F] text-white">
              {isFrench ? "Retour à l'accueil" : "Back to home"}
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#2F4F4F] hover:opacity-80" data-testid="back-home">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">{isFrench ? 'Accueil' : 'Home'}</span>
          </Link>
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#2F4F4F]" />
            <span className="font-heading text-lg text-[#2F4F4F]">{isFrench ? 'Mon Historique' : 'My History'}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link to="/diagnostic">
              <Button className="rounded-full bg-[#2F4F4F] hover:bg-[#2F4F4F]/90 text-white">
                {isFrench ? 'Nouveau diagnostic' : 'New diagnostic'}
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="pt-28 pb-24 px-6">
        <div className="max-w-4xl mx-auto">
          {/* User Info */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-semibold text-[#2D2D2D]">
                {isFrench ? `Bonjour, ${user.name}` : `Hello, ${user.name}`}
              </h1>
              <p className="text-[#5C5C5C]">
                {user.is_premium ? (
                  <span className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-[#E2725B]" />
                    {isFrench ? 'Compte Premium' : 'Premium Account'}
                  </span>
                ) : (
                  isFrench ? 'Compte gratuit' : 'Free Account'
                )}
              </p>
            </div>
            {!user.is_premium && (
              <Link to="/premium">
                <Button className="rounded-full bg-[#E2725B] hover:bg-[#E2725B]/90 text-white">
                  <Crown className="w-4 h-4 mr-2" />
                  {isFrench ? 'Passer à Premium' : 'Upgrade to Premium'}
                </Button>
              </Link>
            )}
          </div>

          {loading ? (
            <div className="text-center py-16">
              <Loader2 className="w-12 h-12 text-[#2F4F4F] animate-spin mx-auto mb-4" />
              <p className="text-[#5C5C5C]">{isFrench ? 'Chargement...' : 'Loading...'}</p>
            </div>
          ) : diagnostics.length === 0 ? (
            <div className="text-center py-16">
              <History className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold text-[#2D2D2D] mb-2">
                {isFrench ? 'Aucun diagnostic encore' : 'No diagnostics yet'}
              </h2>
              <p className="text-[#5C5C5C] mb-6">
                {isFrench ? 'Faites votre premier diagnostic pour commencer !' : 'Take your first diagnostic to get started!'}
              </p>
              <Link to="/diagnostic">
                <Button className="rounded-full bg-[#2F4F4F] text-white">
                  {isFrench ? 'Faire un diagnostic' : 'Take a diagnostic'}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-[#8A8A8A] mb-4">
                {diagnostics.length} {isFrench ? 'diagnostic(s)' : 'diagnostic(s)'}
              </p>
              {diagnostics.map((diagnostic, idx) => (
                <Card key={diagnostic.id || idx} className="bg-white border-gray-100 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-[#FAF9F6] flex items-center justify-center">
                          <span className={`text-2xl font-bold ${getScoreColor(diagnostic.scores?.overall || 0)}`}>
                            {Math.round(diagnostic.scores?.overall || 0)}%
                          </span>
                        </div>
                        <div>
                          <div className="flex items-center gap-2 text-sm text-[#8A8A8A] mb-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(diagnostic.created_at)}
                          </div>
                          <div className="flex items-center gap-2 text-sm">
                            <TrendingUp className="w-4 h-4 text-[#2F4F4F]" />
                            <span className="text-[#5C5C5C]">
                              {isFrench ? 'Score global' : 'Overall score'}: 
                              <span className={`font-semibold ml-1 ${getScoreColor(diagnostic.scores?.overall || 0)}`}>
                                {diagnostic.scores?.overall >= 80 ? (isFrench ? 'Excellent' : 'Excellent') :
                                 diagnostic.scores?.overall >= 60 ? (isFrench ? 'Bon' : 'Good') :
                                 diagnostic.scores?.overall >= 40 ? (isFrench ? 'À développer' : 'To develop') :
                                 (isFrench ? 'Prioritaire' : 'Priority')}
                              </span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <Link to={`/results/${diagnostic.session_id}`}>
                        <Button variant="outline" className="rounded-full border-gray-200">
                          <FileText className="w-4 h-4 mr-2" />
                          {isFrench ? 'Voir détails' : 'View details'}
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
